/**
 * Actual vs Ideal Revenue and Expense Chart
 * Horizontal stacked bars: Revenue (Actual | Recoverable) and Expense (Ideal | Recoverable).
 * Ideal revenue = actual + recoverable; ideal expense = actual - recoverable.
 * @feature BRIDGE_SESSION_FLOW - Entropy heat map financial impact
 */

import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import type { EntropyMapMetrics } from '../../types/bridgeSession';
import styles from './ActualVsIdealChart.module.css';

const COLOR_REVENUE_ACTUAL = '#52c41a';
const COLOR_REVENUE_RECOVERABLE = '#95de64';
const COLOR_EXPENSE_ACTUAL = '#ff4d4f';
const COLOR_EXPENSE_OVERLAY = '#8c8c8c';
/** Expense correction overlay height - shows as subtracting from the actual bar */
const EXPENSE_OVERLAY_HEIGHT = 8;

export interface ActualVsIdealChartProps {
  metrics: EntropyMapMetrics;
  height?: number;
  /** Show future revenue/expense arrows on the bars. Only used on Bridge Card, not Entropy Heat Map. */
  showFutureArrows?: boolean;
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export const ActualVsIdealChart: React.FC<ActualVsIdealChartProps> = ({
  metrics,
  height = 90,
  showFutureArrows = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { revenueBarData, expenseBarData, maxValue, idealRevenue, idealExpense, futureRevenue, futureExpense } = useMemo(() => {
    const actualRev = metrics.current_revenue ?? 0;
    const recoverableRev = metrics.recoverable_revenue ?? 0;
    const idealRev = (metrics.ideal_revenue != null && metrics.ideal_revenue > 0)
      ? metrics.ideal_revenue
      : actualRev + recoverableRev;

    const actualExp = metrics.current_expense ?? 0;
    const recoverableExp = metrics.recoverable_expense ?? 0;
    const idealExp = (metrics.ideal_expense != null && metrics.ideal_expense > 0)
      ? metrics.ideal_expense
      : Math.max(0, actualExp - recoverableExp);

    return {
      revenueBarData: [
        { value: actualRev, color: COLOR_REVENUE_ACTUAL, label: 'Actual' },
        { value: recoverableRev, color: COLOR_REVENUE_RECOVERABLE, label: 'Recoverable' },
      ],
      expenseBarData: [
        { value: idealExp, color: COLOR_EXPENSE_ACTUAL, label: 'Ideal (after savings)' },
        { value: recoverableExp, color: COLOR_EXPENSE_OVERLAY, label: 'Recoverable (waste)' },
      ],
      maxValue: Math.max(
        idealRev,
        actualExp,
        ...(showFutureArrows ? [metrics.future_revenue ?? 0, metrics.future_expense ?? 0] : []),
        1,
      ),
      idealRevenue: idealRev,
      idealExpense: idealExp,
      futureRevenue: showFutureArrows ? metrics.future_revenue : undefined,
      futureExpense: showFutureArrows ? metrics.future_expense : undefined,
    };
  }, [metrics, showFutureArrows]);

  const hasData = (metrics.current_revenue ?? 0) > 0 || (metrics.current_expense ?? 0) > 0;

  const drawChart = React.useCallback(() => {
    if (!svgRef.current || !containerRef.current || !hasData) return;
    const container = containerRef.current;
    const svgEl = svgRef.current;
    if (container.clientWidth <= 0) return;

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    const hasArrows = showFutureArrows && (futureRevenue != null || futureExpense != null);
    const margin = { top: hasArrows ? 20 : 8, right: 8, bottom: 16, left: 100 };
    const width = Math.max(container.clientWidth, 200);
    const innerWidth = width - margin.left - margin.right;
    const barHeight = 22;
    const barGap = 20;
    const revenueBarY = 0;
    const expenseBarY = revenueBarY + barHeight + barGap;

    const x = d3.scaleLinear().domain([0, maxValue]).range([0, innerWidth]);

    const g = svg
      .attr('width', width)
      .attr('height', margin.top + expenseBarY + barHeight + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Revenue bar
    g.append('text')
      .attr('x', -8)
      .attr('y', revenueBarY + barHeight / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '12px')
      .style('fill', '#595959')
      .text('Revenue');

    let offset = 0;
    revenueBarData.forEach((seg) => {
      if (seg.value <= 0) return;
      const segWidth = x(seg.value);
      g.append('rect')
        .attr('x', offset)
        .attr('y', revenueBarY)
        .attr('width', Math.max(2, segWidth))
        .attr('height', barHeight)
        .attr('fill', seg.color)
        .attr('stroke', '#000')
        .attr('stroke-width', 1)
        .attr('rx', 2);
      offset += segWidth;
    });

    if (futureRevenue != null && futureRevenue > 0) {
      const futureX = x(futureRevenue);
      g.append('line')
        .attr('x1', futureX)
        .attr('y1', revenueBarY - 14)
        .attr('x2', futureX)
        .attr('y2', revenueBarY)
        .attr('stroke', '#1890ff')
        .attr('stroke-width', 2);
      g.append('polygon')
        .attr('points', `${futureX},${revenueBarY + 6} ${futureX - 5},${revenueBarY - 4} ${futureX + 5},${revenueBarY - 4}`)
        .attr('fill', '#1890ff')
        .append('title')
        .text(`Future: ${formatCurrency(futureRevenue)}`);
    }

    const expenseIdeal = expenseBarData[0];
    const expenseRecoverable = expenseBarData[1];
    const expenseIdealWidth = expenseIdeal.value > 0 ? x(expenseIdeal.value) : 0;
    const expenseFullWidth = expenseIdealWidth + (expenseRecoverable.value > 0 ? x(expenseRecoverable.value) : 0);

    g.append('text')
      .attr('x', -8)
      .attr('y', expenseBarY + barHeight / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '12px')
      .style('fill', '#595959')
      .text('Expense');

    g.append('rect')
      .attr('x', 0)
      .attr('y', expenseBarY)
      .attr('width', expenseFullWidth)
      .attr('height', barHeight)
      .attr('fill', COLOR_EXPENSE_ACTUAL)
      .attr('stroke', '#000')
      .attr('stroke-width', 1)
      .attr('rx', 2);

    if (expenseRecoverable.value > 0) {
      const overlayWidth = x(expenseRecoverable.value);
      const overlayX = expenseFullWidth - overlayWidth;
      const overlayY = expenseBarY + (barHeight - EXPENSE_OVERLAY_HEIGHT) / 2;
      g.append('rect')
        .attr('x', overlayX)
        .attr('y', overlayY)
        .attr('width', overlayWidth)
        .attr('height', EXPENSE_OVERLAY_HEIGHT)
        .attr('fill', COLOR_EXPENSE_OVERLAY)
        .attr('stroke', '#000')
        .attr('stroke-width', 1)
        .attr('rx', 2);
    }

    if (futureExpense != null && futureExpense > 0) {
      const futureX = x(futureExpense);
      g.append('line')
        .attr('x1', futureX)
        .attr('y1', expenseBarY - 14)
        .attr('x2', futureX)
        .attr('y2', expenseBarY)
        .attr('stroke', '#1890ff')
        .attr('stroke-width', 2);
      g.append('polygon')
        .attr('points', `${futureX},${expenseBarY + 6} ${futureX - 5},${expenseBarY - 4} ${futureX + 5},${expenseBarY - 4}`)
        .attr('fill', '#1890ff')
        .append('title')
        .text(`Future: ${formatCurrency(futureExpense)}`);
    }
  }, [revenueBarData, expenseBarData, maxValue, hasData, futureRevenue, futureExpense, showFutureArrows]);

  useEffect(() => {
    if (!containerRef.current || !hasData) return;

    const runDraw = () => {
      requestAnimationFrame(() => {
        if (containerRef.current && svgRef.current) drawChart();
      });
    };

    runDraw();

    const ro = new ResizeObserver(runDraw);
    ro.observe(containerRef.current);

    const onBeforePrint = () => {
      requestAnimationFrame(() => setTimeout(runDraw, 100));
    };
    window.addEventListener('beforeprint', onBeforePrint);

    return () => {
      ro.disconnect();
      window.removeEventListener('beforeprint', onBeforePrint);
    };
  }, [drawChart, hasData]);

  if (!hasData) return null;

  return (
    <div className={styles.chartWrap}>
      <div className={styles.chartLabel}>Actual vs ideal</div>
      <div ref={containerRef} className={styles.chartContainer}>
        <svg ref={svgRef} />
      </div>
      <div className={styles.summary}>
        <span>
          Revenue: <strong>{formatCurrency(metrics.current_revenue ?? 0)}</strong> actual →{' '}
          <strong>{formatCurrency(idealRevenue)}</strong> ideal
          (+{formatCurrency(metrics.recoverable_revenue ?? 0)} recoverable)
        </span>
        <span>
          Expense: <strong>{formatCurrency(metrics.current_expense ?? 0)}</strong> actual →{' '}
          <strong>{formatCurrency(idealExpense)}</strong> ideal
          (−{formatCurrency(metrics.recoverable_expense ?? 0)} recoverable)
        </span>
      </div>
    </div>
  );
};

export default ActualVsIdealChart;
